import { FC, useEffect, useState } from 'react'
import './App.css'
import AppRoutes from './utils/AppRoutes'
import { LogoutTimer } from './components'
import { Route, Routes, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from './store/hooks'
import { getBusinessUnitsInnovus } from './services/BusinessUnitServices'
import {
  alertService,
  getAllCities,
  getCities,
  getCitiesByBu,
  getTaxIdentificationTypes,
} from './services'
import {
  setInmutableCities,
  setInmutableOriginCities,
  setInmutableBusinessUnits,
  setInmutablePieceCategories,
  setInmutableTaxIdentificationTypes,
  setInmutableCitiesByBu,
  setInmutableRangeDeclareList,
  setInmutableExchangeRate,
  setVolumenMin,
  setDensityMax,
  setHighDeclaredValue,
  setInmutableAppData,
} from './store/slices/inmutable'
import { getBaseAppData } from './services/applicationDataService'
import { CategoryMapper } from './utils/mappers'
import { AppDataInterface } from './interfaces/AppDataInterface'
import { initializeToken } from './services/tokenService'
import { useBulkUploadCompletionNotifier } from './hooks/useBulkUploadCompletionNotifier'
import { UpdateModal } from './components/Atoms/UpdateModalProps '

const App: FC = () => {
  const dispatch = useAppDispatch()
  const logged = useAppSelector((state) => state.user.logged)
  const location = useLocation()
  const [showUpdateModal, setShowUpdateModal] = useState(false)

  // Notifica al completar una importación masiva. Limitado a las vistas de carga
  // masiva (/shipment/bulk-upload*): solo pollea shipment-bulk/list mientras estás
  // en esas pantallas, no en toda la app.
  const isBulkUploadRoute = location.pathname.startsWith('/shipment/bulk-upload')
  useBulkUploadCompletionNotifier(logged && isBulkUploadRoute)

  useEffect(() => {
    const checkVersion = () => {
      const currentVersion = process.env.REACT_APP_VERSION || '1.0.0'
      const savedVersion = localStorage.getItem('appVersion')

      if (!savedVersion) {
        // Primera carga, solo guardamos la versión
        localStorage.setItem('appVersion', currentVersion)
        window.location.reload()
      } else if (savedVersion !== currentVersion) {
        // Versión diferente detectada
        setShowUpdateModal(true)
      }
    }

    // Verificar al montar el componente
    checkVersion()

    // Verificar periódicamente (cada 5 minutos)
    const versionCheckInterval = setInterval(checkVersion, 5 * 60 * 1000)

    return () => clearInterval(versionCheckInterval)
  }, [])

  useEffect(() => {
    if (!logged) return

    // Función para cargar todos los datos necesarios cuando el usuario está logueado
    const loadAllData = async () => {
      try {
        // Cargar datos en paralelo cuando sea posible
        const [
          taxIdTypes,
          businessUnits,
          cities,
          originCities,
          citiesByBu,
          appDataResponse,
        ] = await Promise.all([
          getTaxIdentificationTypes(),
          getBusinessUnitsInnovus(),
          getCities(),
          getAllCities(),
          getCitiesByBu(),
          getBaseAppData(null),
        ])

        // Dispatch de los datos obtenidos
        if (taxIdTypes) dispatch(setInmutableTaxIdentificationTypes(taxIdTypes))
        if (businessUnits) dispatch(setInmutableBusinessUnits(businessUnits))
        if (cities) dispatch(setInmutableCities(cities))
        if (originCities) dispatch(setInmutableOriginCities(originCities))
        if (citiesByBu) dispatch(setInmutableCitiesByBu(citiesByBu))

        // Procesar los datos de la aplicación
        if (!appDataResponse.didError && appDataResponse.model) {
          const appDataDto = appDataResponse.model

          dispatch(
            setInmutableAppData({
              applicationName: appDataDto.applicationName,
              applicationID: appDataDto.applicationID,
              applicationDescription: appDataDto.applicationDescription,
              applicationCode: appDataDto.applicationCode,
            } as AppDataInterface)
          )

          if (appDataDto.rangeDeclareList) {
            dispatch(setInmutableRangeDeclareList(appDataDto.rangeDeclareList))
          }
          if (appDataDto.exchangeRate) {
            dispatch(setInmutableExchangeRate(appDataDto.exchangeRate))
          }
          if (appDataDto.volumenMin) {
            dispatch(setVolumenMin(appDataDto.volumenMin))
          }
          if (appDataDto.densityMax) {
            dispatch(setDensityMax(appDataDto.densityMax))
          }
          if (appDataDto.highDeclaredValue) {
            dispatch(setHighDeclaredValue(appDataDto.highDeclaredValue?.[0]))
          }
          if (appDataDto.shippingCategoryList) {
            const categoriesMapper =
              appDataDto.shippingCategoryList.map(CategoryMapper)
            dispatch(setInmutablePieceCategories(categoriesMapper))
          }
        } else if (appDataResponse.errorMessage) {
          alertService.error(
            'Error al cargar los datos de la aplicación',
            appDataResponse.errorMessage,
            { autoClose: false }
          )
        }
      } catch (error) {
        console.error('Error loading application data:', error)
        alertService.error(
          'Error al cargar los datos de la aplicación',
          'Ocurrió un error inesperado',
          { autoClose: false }
        )
      }
    }

    loadAllData()
  }, [logged, dispatch])

  useEffect(() => {
    // Inicializar el token cuando la app carga
    initializeToken().catch((error) => {
      console.error('Failed to initialize token:', error)
    })

    // Configurar intervalo para renovar el token cada 30 minutos
    const interval = setInterval(() => {
      if (logged) {
        initializeToken().catch((error) => {
          console.error('Failed to refresh token:', error)
        })
      }
    }, 30 * 60 * 1000) // 30 minutos

    return () => clearInterval(interval)
  }, [])

  const handleReload = () => {
    const currentVersion = process.env.REACT_APP_VERSION || '1.0.0'
    localStorage.setItem('appVersion', currentVersion)
    window.location.reload()
  }

  return (
    <>
      <Routes>
        {AppRoutes.filter((r) => r.auth == !!logged).map((route, index) => {
          const { element, ...rest } = route
          return <Route key={index} {...rest} element={element} />
        })}
      </Routes>
      <LogoutTimer />

      {/* Modal de actualización */}
      <UpdateModal
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onReload={handleReload}
      />
    </>
  )
}

export default App
